import React, { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);

const readLS = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => readLS("dg_cart", []));
  const [wishlist, setWishlist] = useState(() => readLS("dg_wishlist", []));
  const [isCartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("dg_cart", JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem("dg_wishlist", JSON.stringify(wishlist));
  }, [wishlist]);

  const addToCart = (product, size, qty = 1) => {
    const selectedSize = size || (product.sizes && product.sizes[0]) || "Default";
    setItems((prev) => {
      const key = `${product.id}-${selectedSize}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      }
      return [
        ...prev,
        {
          key,
          id: product.id,
          name: product.name,
          slug: product.slug,
          price: product.price,
          mrp: product.mrp,
          image: product.image,
          size: selectedSize,
          qty,
        },
      ];
    });
    setCartOpen(true);
  };

  const removeFromCart = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const updateQty = (key, qty) => {
    if (qty < 1) return removeFromCart(key);
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, qty } : i)));
  };

  const clearCart = () => setItems([]);

  const toggleWishlist = (id) =>
    setWishlist((prev) => (prev.includes(id) ? prev.filter((w) => w !== id) : [...prev, id]));

  const cartCount = items.reduce((s, i) => s + i.qty, 0);
  const cartTotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  const cartMrpTotal = items.reduce((s, i) => s + i.mrp * i.qty, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        wishlist,
        isCartOpen,
        setCartOpen,
        addToCart,
        removeFromCart,
        updateQty,
        clearCart,
        toggleWishlist,
        cartCount,
        cartTotal,
        cartMrpTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
